import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EditRequest, ReservationRow } from '@smile/shared';
import { ApiError, api } from '../api/client';

/**
 * "Opravit údaje" — the legacy edit form's free-text fields, on the
 * reservation detail. Opens as a card with the same fields in the same
 * order the legacy form had them; Uložit sends only the fields that
 * changed, and the server journals each one old → new. Dates, prices and
 * status are not here: those are Phase 1b (pricelists) and have rules of
 * their own.
 */
type Draft = {
  driverName: string;
  driverPhone: string;
  driverEmail: string;
  car: string;
  spz: string;
  flightNumber: string;
  note: string;
  keycafeArrival: string;
  keycafeDeparture: string;
  spot: string;
  parkingTarget: string;
  person: string;
  invoiceCompany: string;
  invoiceStreet: string;
  invoiceCity: string;
  invoicePsc: string;
  invoiceIc: string;
  invoiceDic: string;
  partnerRef: string;
};

function draftOf(r: ReservationRow): Draft {
  return {
    driverName: r.driverName ?? '',
    driverPhone: r.driverPhone ?? '',
    driverEmail: r.driverEmail ?? '',
    car: r.car ?? '',
    spz: r.spz ?? '',
    flightNumber: r.flightNumber ?? '',
    note: r.note ?? '',
    keycafeArrival: r.keycafeArrival ?? '',
    keycafeDeparture: r.keycafeDeparture ?? '',
    spot: r.parking ?? '',
    parkingTarget: r.parkingTarget ?? '',
    person: r.person == null ? '' : String(r.person),
    invoiceCompany: r.invoiceCompany ?? '',
    invoiceStreet: r.invoiceStreet ?? '',
    invoiceCity: r.invoiceCity ?? '',
    invoicePsc: r.invoicePsc ?? '',
    invoiceIc: r.invoiceIc ?? '',
    invoiceDic: r.invoiceDic ?? '',
    partnerRef: r.partnerRef ?? '',
  };
}

export function EditForm({ r, onClose }: { r: ReservationRow; onClose: () => void }) {
  const qc = useQueryClient();
  const base = draftOf(r);
  const [d, setD] = useState<Draft>(base);
  const spots = useQuery({ queryKey: ['catalog', 'spots'], queryFn: () => api.spots(), staleTime: 10 * 60_000 });

  const diff = (): EditRequest => {
    const out: EditRequest = {};
    for (const k of Object.keys(d) as Array<keyof Draft>) {
      if (d[k].trim() === base[k].trim()) continue;
      if (k === 'person') out.person = d.person.trim() === '' ? 0 : Number(d.person);
      else out[k] = d[k].trim();
    }
    return out;
  };
  const pending = Object.keys(diff()).length;

  const m = useMutation({
    mutationFn: () => api.edit(r.id, diff()),
    onSuccess: async () => {
      await qc.invalidateQueries();
      onClose();
    },
  });

  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ??
        (err.code === 'writes_disabled'
          ? 'Zápisy jsou na tomto serveru vypnuté.'
          : err.code === 'forbidden'
            ? 'Na tuhle akci nemáte oprávnění.'
            : err.message)
      : err instanceof Error
        ? err.message
        : null;

  const field = (label: string, k: keyof Draft, extra?: React.InputHTMLAttributes<HTMLInputElement>) => (
    <label className="ef">
      {label}
      <input value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value })} {...extra} />
    </label>
  );

  const onSite = !!r.arrivalReal && !r.departureReal && !!base.spot;
  const targets = [
    ...(spots.data?.tuchomerice ?? []).map((v) => ({ value: v, label: v })),
    ...(spots.data?.europort ?? []),
  ];

  return (
    <form
      className="card detailcard editform"
      onSubmit={(e) => {
        e.preventDefault();
        if (!m.isPending && pending > 0) m.mutate();
      }}
    >
      <h2>Opravit údaje</h2>
      <div className="efgrid">
        {field('Jméno', 'driverName', { maxLength: 100, autoFocus: true })}
        {field('Telefon', 'driverPhone', { maxLength: 32 })}
        {field('E-mail', 'driverEmail', { maxLength: 120, type: 'email' })}
        {field('Auto', 'car', { maxLength: 120 })}
        {field('SPZ', 'spz', { maxLength: 12 })}
        {field('Číslo letu', 'flightNumber', { maxLength: 32 })}
        {field('KeyCafe příjezd', 'keycafeArrival', { maxLength: 8, pattern: '[A-Za-z0-9-]{0,8}' })}
        {field('KeyCafe odjezd', 'keycafeDeparture', { maxLength: 8, pattern: '[A-Za-z0-9-]{0,8}' })}
        <label className="ef">
          Stání
          {/* A car on site cannot lose its spot (no list would show it); the
              location follows the spot on the server, as on Přijel. */}
          <select value={d.spot} onChange={(e) => setD({ ...d, spot: e.target.value })}>
            {!onSite && <option value="">—</option>}
            {d.spot && !targets.some((t) => t.value === d.spot) && <option value={d.spot}>{d.spot}</option>}
            {targets.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="ef">
          Doporučené místo
          <select value={d.parkingTarget} onChange={(e) => setD({ ...d, parkingTarget: e.target.value })}>
            <option value="">—</option>
            {d.parkingTarget && !targets.some((t) => t.value === d.parkingTarget) && (
              <option value={d.parkingTarget}>{d.parkingTarget}</option>
            )}
            {targets.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        {field('Osob', 'person', { inputMode: 'numeric', pattern: '[0-9]{0,2}', maxLength: 2 })}
        <label className="ef wide">
          Poznámka
          <textarea value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} maxLength={2000} rows={3} />
        </label>
      </div>
      <h2>Fakturační údaje</h2>
      <div className="efgrid">
        {field('Firma', 'invoiceCompany', { maxLength: 255 })}
        {field('Ulice', 'invoiceStreet', { maxLength: 255 })}
        {field('Město', 'invoiceCity', { maxLength: 255 })}
        {field('PSČ', 'invoicePsc', { maxLength: 8 })}
        {field('IČ', 'invoiceIc', { maxLength: 63 })}
        {field('DIČ', 'invoiceDic', { maxLength: 63 })}
        {field('Reference partnera', 'partnerRef', { maxLength: 16 })}
      </div>
      <div className="efactions">
        <button type="submit" className="btn primary" disabled={m.isPending || pending === 0}>
          {m.isPending ? 'Ukládám…' : pending ? `Uložit (${pending})` : 'Beze změn'}
        </button>
        <button type="button" className="btn" onClick={onClose} disabled={m.isPending}>
          Zrušit
        </button>
        {message && (
          <span className="arrive-error" role="alert">
            {message}
          </span>
        )}
      </div>
    </form>
  );
}
